import { gql } from '@apollo/client';
import DocumentViewer from '@orn/shared-web/components/DocumentViewer';
import {
  getDocumentDisplayNames,
  sortDocumentsBySequence,
  type IViewerMetadata,
} from '@orn/shared-web/components/DocumentViewer/documentUtils';
import { ChevronRightIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { BillSource } from '../../../__generated__/globalTypes';
import { formatDateAtTime } from '../../../utils/formatting';
import RecordPane from '../../entity/RecordPane';

import DocumentTile from './DocumentTile';

import type { AttachmentsSectionBillFragment } from './__generated__/AttachmentsSection.gql';

export const AttachmentsSectionBillFragmentDocument = gql`
  fragment AttachmentsSectionBill on Bill {
    id
    source
    senderIdentifier
    createdAt
    documents {
      id
      name
      mimeType
      sequenceNumber
      assetUrl
      previewUrl
      emailMetadata {
        originalFrom
        originalTo
        originalSubject
      }
    }
  }
`;

export type AttachmentsSectionDocument = AttachmentsSectionBillFragment['documents'][number];

/** The bill facts the viewer's rail shows above its documents: how the bill arrived, from whom, and when. */
export interface IAttachmentsBillMetadata {
  source: BillSource;
  senderIdentifier?: string | null;
  createdAt?: string | null;
}

function formatBillSource(source: BillSource): string {
  switch (source) {
    case BillSource.Email:
      return 'Email';
    case BillSource.Sms:
      return 'SMS';
    default:
      return 'Manual';
  }
}

/** Builds the metadata block `DocumentViewer` shows in its rail from the bill the documents belong to. */
export function buildDocumentViewerMetadata(bill: IAttachmentsBillMetadata): IViewerMetadata {
  return {
    source: formatBillSource(bill.source),
    senderIdentifier: bill.senderIdentifier ?? null,
    date: bill.createdAt ? formatDateAtTime(bill.createdAt) : null,
  };
}

export interface IAttachmentsSectionProps {
  /** The record's bill, as the read returns it — `null` where the record has none. */
  bill: AttachmentsSectionBillFragment | null | undefined;
  className?: string;
}

/**
 * The "Attachments" section of a record page (Figma "Transaction/Card/
 * Attachments"): one row per document on the record's bill — a type tile,
 * the filename, a chevron — that opens the document viewer at that document.
 * Hidden when the record has no bill or the bill has no documents. Pure: the
 * page query spreads `AttachmentsSection.Fragment` and hands the bill down.
 *
 * The viewer mounts only while a row is open, so every open starts at the
 * clicked document rather than at whatever the rail last selected.
 */
const AttachmentsSection = ({ bill, className }: IAttachmentsSectionProps) => {
  const [openDocumentId, setOpenDocumentId] = React.useState<string | null>(null);
  const documents = React.useMemo(() => (bill ? sortDocumentsBySequence(bill.documents) : []), [bill]);
  const displayNames = React.useMemo(() => getDocumentDisplayNames(documents), [documents]);
  const metadata = React.useMemo(() => (bill ? buildDocumentViewerMetadata(bill) : null), [bill]);
  const closeViewer = React.useCallback(() => setOpenDocumentId(null), []);

  if (!bill || documents.length === 0) {
    return null;
  }

  return (
    <>
      <RecordPane.Section title="Attachments" className={className}>
        {documents.map((document) => (
          <DocumentRow
            key={document.id}
            document={document}
            name={displayNames.get(document.id) ?? document.id}
            onOpen={() => setOpenDocumentId(document.id)}
          />
        ))}
      </RecordPane.Section>
      {openDocumentId !== null && (
        <DocumentViewer
          documents={documents}
          metadata={metadata}
          isOpen
          initialDocumentId={openDocumentId}
          onClose={closeViewer}
        />
      )}
    </>
  );
};

interface IDocumentRowProps {
  document: AttachmentsSectionDocument;
  name: string;
  onOpen: () => void;
}

/**
 * One 64px row (Figma "Transaction/Attachment"). The button is an overlay
 * across the row rather than a wrapper, the way `RecordPane.Row` links, so a
 * later interactive addon beside the name never nests inside it.
 */
const DocumentRow = ({ document, name, onOpen }: IDocumentRowProps) => {
  return (
    <div className="hover:bg-utility-brand-25 relative flex h-16 items-center gap-2 px-4 transition-colors">
      <button
        type="button"
        onClick={onOpen}
        aria-label={name}
        className="outline-focus-ring absolute inset-0 cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2"
      />
      <DocumentTile document={document} />
      <span className="text-secondary text-reg min-w-0 flex-1 truncate leading-5">{name}</span>
      <ChevronRightIcon aria-hidden="true" className="text-quaternary m-0.5 size-5 shrink-0" />
    </div>
  );
};

export default Object.assign(AttachmentsSection, { Fragment: AttachmentsSectionBillFragmentDocument });
